/** @format */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { UserInfo } from './userInfo';
import { useTranslation } from 'react-i18next';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import { ExtLinkIcon, PlusIcon, SearchIcon } from 'src/components/icons';
import { buildSearchUrlSearch, SEARCH_URL_DEFAULTS } from 'src/utils/searchUrl';
import useAuth from 'src/hooks/useAuth';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import BrandOfAumovio from './brandOfAumovio';
import Search from './search';
import Logo from './logo';
import { IS_PRODUCTION } from 'src/constants';
import { checkCOntentEditorRole, checkAdminRole } from 'src/utils/administration';
import { useDispatch } from 'src/store';
import { clearContentItems, setSelectedLanguage } from 'src/slices/topics';
import { resolveSelectedLanguage } from 'src/utils/locale';

const headerMenuLinks = [
  { to: '/dashboard', labelKey: 'menu.dashboard' },
  { to: '/news', labelKey: 'menu.news' },
  { to: '/extranet', labelKey: 'menu.extranet', state: { resetExtranetTopicNav: true } }
];

const administrationMenuLinks = [
  { to: '/administration/topics', labelKey: 'menu.topics', requiresAdmin: true },
  { to: '/administration/contents', labelKey: 'menu.contents' }
];

const administrationCreateLinks = [
  { to: '/administration/content/create', labelKey: 'menu.createNewPage' },
  { to: '/administration/topics/create', labelKey: 'menu.createNewTopics', requiresAdmin: true }
];

const ourPlatformsMenuLinks = [
  {
    href: IS_PRODUCTION ? 'https://academy.ate-vdo.com/' : 'https://uat-continental.crm-e.net/',
    labelKey: 'menu.platforms.academy'
  },
  {
    href: IS_PRODUCTION ? 'https://shop.ate.de/' : 'https://mcstaging.shop.ate.de/',
    labelKey: 'menu.platforms.ateShop'
  },
  {
    href: 'https://partner.ate-vdo.com/',
    labelKey: 'menu.platforms.partnerProgram'
  },
  {
    href: 'https://ate-vdo.shop-pieper.com/home',
    labelKey: 'menu.platforms.promotionalItemsShop'
  }
];

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

type HeaderProps = {
  getSelectLang?: (event: { target: { value: string } }) => void | Promise<void>;
  userLang?: string;
};

const Header = (props: HeaderProps) => {
  const { getSelectLang, userLang } = props;
  const { logout, user } = useAuth();
  const { trackLogout } = useGa4Analytics();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const hasAdminRole = checkAdminRole(user);
  const hasContentEditorRole = checkCOntentEditorRole(user);
  const visibleHamburgerLinks = [
    ...headerMenuLinks,
    ...(hasAdminRole || hasContentEditorRole
      ? administrationMenuLinks.filter(({ requiresAdmin }) => !requiresAdmin || hasAdminRole)
      : [])
  ];

  const preLang = usePrevious(userLang);
  const headerRef = useRef<HTMLElement | null>(null);

  const handleExtranetNavClick = () => {
    dispatch(setSelectedLanguage(resolveSelectedLanguage()));
    dispatch(clearContentItems());
  };

  const timeout = 1000 * 30 * 10;
  const [, setRemaining] = useState(timeout);
  const [, setElapsed] = useState(0);
  const [, setLastActive] = useState<number | Date>(+new Date());
  const [, setIsIdle] = useState(false);

  const handleOnActive = () => setIsIdle(false);
  const handleOnIdle = () => setIsIdle(true);

  const { getRemainingTime, getLastActiveTime, getElapsedTime } = useIdleTimer({
    timeout,
    onActive: handleOnActive,
    onIdle: handleOnIdle
  });

  const handleLogout = async () => {
    trackLogout();
    await logout();
  };

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    const keyword = searchQuery.trim();
    if (!keyword) return;
    navigate(`/search${buildSearchUrlSearch({ ...SEARCH_URL_DEFAULTS, keywords: [keyword] })}`);
    setSearchQuery('');
  };

  useEffect(() => {
    if (preLang && preLang !== userLang && window.location.pathname === '/dashboard') {
    }
    // eslint-disable-next-line
  }, [userLang]);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return;

    const EXTRA_HIDE_PX = 10; // Hide a bit more than header height for a cleaner reveal.

    let lastScrollY = window.pageYOffset || 0;
    let hiddenPx = 0;
    let maxHidePx = headerEl.getBoundingClientRect().height + EXTRA_HIDE_PX;

    const apply = () => {
      headerEl.style.top = `${-hiddenPx}px`;
      const headerRect = headerEl.getBoundingClientRect();
      const headerBottom = Math.max(0, headerRect.bottom);

      document.documentElement.style.setProperty('--header-full-height', `${headerEl.offsetHeight}px`);
      document.documentElement.style.setProperty('--header-hidden-px', `${hiddenPx}px`);
      document.documentElement.style.setProperty('--header-bottom', `${headerBottom}px`);
      window.dispatchEvent(new CustomEvent('header-scroll-layout'));
    };

    const recalc = () => {
      maxHidePx = headerEl.getBoundingClientRect().height + EXTRA_HIDE_PX;
      hiddenPx = Math.min(hiddenPx, maxHidePx);
      apply();
    };

    let rafId = 0;
    const onScrollRaf = () => {
      rafId = 0;
      const currentY = window.pageYOffset || 0;
      const deltaY = currentY - lastScrollY;
      lastScrollY = currentY;

      // Always reset when the user goes back to the very top.
      if (currentY <= 0) {
        hiddenPx = 0;
        apply();
        return;
      }

      hiddenPx = Math.max(0, Math.min(maxHidePx, hiddenPx + deltaY));
      apply();
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(onScrollRaf);
    };

    // Init state.
    apply();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', recalc);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', recalc);
      if (rafId) window.cancelAnimationFrame(rafId);
      document.documentElement.style.removeProperty('--header-bottom');
      document.documentElement.style.removeProperty('--header-hidden-px');
      document.documentElement.style.removeProperty('--header-full-height');
    };
  }, []);

  useEffect(() => {
    setRemaining(getRemainingTime());
    setLastActive(getLastActiveTime());
    setElapsed(getElapsedTime());

    const interval = setInterval(() => {
      setRemaining(getRemainingTime());
      setLastActive(getLastActiveTime());
      setElapsed(getElapsedTime());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <header ref={headerRef}>
      <div className="container">
        <div className="main-logo">
          <Link to="/dashboard">
            <Logo />
          </Link>
        </div>

        <div className="header-links">
          {headerMenuLinks.map(({ to, labelKey, state }) => (
            <Link
              key={to}
              className="button"
              to={to}
              state={state}
              onClick={to === '/extranet' ? handleExtranetNavClick : undefined}>
              {t(labelKey)}
            </Link>
          ))}
          {hasAdminRole || hasContentEditorRole ? (
            <CustomDropDown
              placement="bottom"
              overlayClassName="userInfoDropDown"
              trigger={
                <button
                  type="button"
                  className="button administration-dropdown-button dropdown-button ghost-button"
                  aria-label={t('menu.administration')}
                  aria-haspopup="menu">
                  {t('menu.administration')}
                </button>
              }>
              <ul className="custom-dropdown__menu" role="menu">
                {administrationMenuLinks
                  .filter(({ requiresAdmin }) => !requiresAdmin || hasAdminRole)
                  .map(({ to, labelKey }) => (
                    <li key={to} className="custom-dropdown__menu-item" role="none">
                      <Link role="menuitem" to={to}>
                        {t(labelKey)}
                      </Link>
                    </li>
                  ))}
                <span className="separate"></span>
                {administrationCreateLinks
                  .filter(({ requiresAdmin }) => !requiresAdmin || hasAdminRole)
                  .map(({ to, labelKey }) => (
                    <li key={to} className="custom-dropdown__menu-item" role="none">
                      <Link role="menuitem" to={to} className="create-link">
                        <PlusIcon aria-hidden />
                        {t(labelKey)}
                      </Link>
                    </li>
                  ))}
              </ul>
            </CustomDropDown>
          ) : null}
          <CustomDropDown
            placement="bottom"
            overlayClassName="userInfoDropDown"
            trigger={
              <button
                type="button"
                className="button our-platforms-dropdown-button dropdown-button ghost-button"
                aria-label={t('menu.ourPlatforms')}
                aria-haspopup="menu">
                {t('menu.ourPlatforms')}
              </button>
            }>
            <ul className="custom-dropdown__menu custom-dropdown__menu--platforms" role="menu">
              {ourPlatformsMenuLinks.map(({ href, labelKey }) => (
                <li key={href} className="custom-dropdown__menu-item" role="none">
                  <a href={href} target="_blank" rel="noopener noreferrer" className="label-with-icon">
                    <ExtLinkIcon width={12} height={12} /> {t(labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </CustomDropDown>
        </div>
        <div className="user-box">
          <UserInfo
            data={user}
            logout={handleLogout}
            onLangChange={data => {
              getSelectLang({ target: { value: data } });
            }}
          />
        </div>
        <div className="search-box">
          <Link to="/search" className="search-box__link" aria-label={t('common.search')}>
            <SearchIcon />
          </Link>
          <form onSubmit={handleSearchSubmit} className="search-box__form">
            <input
              type="search"
              name="q"
              className="search-page__query-input"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder={t('administrationPage.extranetContents.searchPlaceholder')}
              aria-label={t('administrationPage.extranetContents.searchPlaceholder')}
            />
            <button type="submit" className="search-page__query-submit" aria-label={t('common.search')}>
              <SearchIcon />
            </button>
          </form>
        </div>

        <div className="hamburger-menu">
          <CustomDropDown
            placement="bottomRight"
            overlayClassName="userInfoDropDown"
            trigger={
              <div role="button" tabIndex={0} className="header-hamburger hamberger-nav">
                <i></i>
                <i></i>
                <i></i>
              </div>
            }>
            <ul className="custom-dropdown__menu custom-dropdown__menu--platforms" role="menu">
              {visibleHamburgerLinks.map(({ to, labelKey, state }, index) => (
                <li key={to} className="custom-dropdown__menu-item" role="none">
                  <Link to={to} state={state} onClick={to === '/extranet' ? handleExtranetNavClick : undefined}>
                    {t(labelKey)}
                  </Link>
                  {index < visibleHamburgerLinks.length - 1 && <span className="separate"></span>}
                </li>
              ))}
              <li className="custom-dropdown__menu-item custom-dropdown__menu-item--platforms" role="none">
                <span className="separate"></span>
                <span className="custom-dropdown__menu-section-label">{t('menu.ourPlatforms')}</span>
                {ourPlatformsMenuLinks.map(({ href, labelKey }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="label-with-icon"
                    role="menuitem">
                    <ExtLinkIcon width={12} height={12} /> {t(labelKey)}
                  </a>
                ))}
              </li>
            </ul>
          </CustomDropDown>
        </div>
      </div>
      <div className="header-bottom">
        <div className="container">
          <BrandOfAumovio />
        </div>
      </div>
    </header>
  );
};

export { Header };
